import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import Reveal from "./Reveal";

/* ---------------------------------------------------------------- layout */

/** Full-bleed editorial band with an optional surface tint. */
export function Section({
  children,
  tone = "default",
  tight = false,
  component = "section",
  className = "",
  sx,
  contentSx,
  ...rest
}) {
  const toneClass =
    tone === "tint"
      ? " app-section--tint"
      : tone === "contrast"
        ? " app-section--contrast"
        : "";

  return (
    <Box
      component={component}
      className={`app-section${tight ? " app-section--tight" : ""}${toneClass}${
        className ? ` ${className}` : ""
      }`}
      sx={sx}
      {...rest}
    >
      <Box className="app-section-inner" sx={contentSx}>
        {children}
      </Box>
    </Box>
  );
}

/** Animated gradient wash used behind hero areas. */
export function HeroBackdrop({ grid = true }) {
  return (
    <Box className="app-hero-backdrop" aria-hidden="true">
      {grid ? <Box className="app-hero-grid" /> : null}
    </Box>
  );
}

/* ------------------------------------------------------------ typography */

export function Eyebrow({ children, color, sx, ...rest }) {
  if (!children) return null;
  return (
    <Typography
      component="p"
      className="app-eyebrow"
      sx={{ color, ...sx }}
      {...rest}
    >
      {children}
    </Typography>
  );
}

export function DisplayTitle({ children, component = "h1", sx, ...rest }) {
  return (
    <Typography component={component} className="app-display" sx={sx} {...rest}>
      {children}
    </Typography>
  );
}

export function Headline({ children, component = "h2", sx, ...rest }) {
  return (
    <Typography
      component={component}
      className="app-headline"
      sx={sx}
      {...rest}
    >
      {children}
    </Typography>
  );
}

export function Lead({ children, component = "p", sx, ...rest }) {
  if (!children) return null;
  return (
    <Typography component={component} className="app-lead" sx={sx} {...rest}>
      {children}
    </Typography>
  );
}

export function GradientText({ children, component = "span", sx, ...rest }) {
  return (
    <Typography
      component={component}
      className="app-gradient-text"
      sx={{ display: "inline", font: "inherit", ...sx }}
      {...rest}
    >
      {children}
    </Typography>
  );
}

/**
 * Editorial section title: eyebrow + headline + lead, with an optional
 * right-aligned action on the same baseline (Apple's section header block).
 */
export function SectionHeading({
  eyebrow,
  title,
  lead,
  action,
  align = "left",
  maxWidth = 760,
  sx,
}) {
  const centered = align === "center";
  return (
    <Reveal>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: centered ? "column" : "row" },
          alignItems: centered
            ? "center"
            : { xs: "flex-start", md: "flex-end" },
          justifyContent: "space-between",
          gap: { xs: 2, md: 4 },
          textAlign: centered ? "center" : "left",
          mb: { xs: 4, md: 6 },
          ...sx,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            maxWidth,
            mx: centered ? "auto" : 0,
          }}
        >
          <Eyebrow>{eyebrow}</Eyebrow>
          {title ? <Headline>{title}</Headline> : null}
          <Lead sx={centered ? { mx: "auto" } : undefined}>{lead}</Lead>
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Box>
    </Reveal>
  );
}

/* --------------------------------------------------------------- actions */

/** Apple pill button. `tone`: primary | secondary | ghost. */
export function CTAButton({
  tone = "primary",
  size = "large",
  children,
  sx,
  ...rest
}) {
  const shared = {
    borderRadius: 980,
    px: { xs: 3, sm: 3.5 },
    fontWeight: 500,
    whiteSpace: "nowrap",
  };

  if (tone === "secondary") {
    return (
      <Button
        variant="outlined"
        size={size}
        sx={{ ...shared, ...sx }}
        {...rest}
      >
        {children}
      </Button>
    );
  }

  if (tone === "ghost") {
    return (
      <Button
        variant="text"
        size={size}
        endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: "0.72rem" }} />}
        sx={{ ...shared, px: 1.5, ...sx }}
        {...rest}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button variant="contained" size={size} sx={{ ...shared, ...sx }} {...rest}>
      {children}
    </Button>
  );
}

/** Text link with the Apple chevron affordance. */
export function ArrowLink({ children, sx, ...rest }) {
  return (
    <Box component="a" className="app-arrow-link" sx={sx} {...rest}>
      {children}
      <ArrowForwardIosRoundedIcon sx={{ fontSize: "0.66rem" }} />
    </Box>
  );
}

export function Pill({ children, accent = false, icon = null, sx, ...rest }) {
  if (!children) return null;
  return (
    <Box
      component="span"
      className={`app-pill${accent ? " app-pill--accent" : ""}`}
      sx={sx}
      {...rest}
    >
      {icon}
      {children}
    </Box>
  );
}

/* -------------------------------------------------------------- surfaces */

export function GlassPanel({ children, className = "", sx, ...rest }) {
  return (
    <Box
      className={`app-glass${className ? ` ${className}` : ""}`}
      sx={{ p: { xs: 2.5, md: 4 }, ...sx }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/** Repeatable feature card: icon + title + description + optional footer. */
export function FeatureTile({
  icon,
  title,
  description,
  footer,
  tone = "paper",
  interactive = true,
  delay = 0,
  sx,
}) {
  return (
    <Reveal delay={delay} style={{ height: "100%" }}>
      <Box
        className={`app-tile${tone === "tint" ? " app-tile--tint" : ""}${
          interactive ? " app-tile--interactive" : ""
        }`}
        sx={sx}
      >
        <Stack spacing={1.5} sx={{ height: "100%" }}>
          {icon ? (
            <Box
              aria-hidden="true"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 46,
                height: 46,
                mb: 0.5,
                borderRadius: "14px",
                color: "primary.main",
                bgcolor: "var(--app-primary-soft)",
                "& .MuiSvgIcon-root": { fontSize: 24 },
              }}
            >
              {icon}
            </Box>
          ) : null}
          {title ? (
            <Typography
              variant="h6"
              component="h3"
              sx={{ letterSpacing: "-0.014em" }}
            >
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography variant="body1" color="text.secondary">
              {description}
            </Typography>
          ) : null}
          {footer ? <Box sx={{ mt: "auto", pt: 1.5 }}>{footer}</Box> : null}
        </Stack>
      </Box>
    </Reveal>
  );
}

/** Large numeric proof point. */
export function StatTile({ value, label, hint, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <Stack spacing={0.5}>
        <Typography
          component="p"
          sx={{
            fontFamily: "var(--app-font-display)",
            fontSize: "clamp(2rem, 1.2rem + 2.4vw, 3rem)",
            fontWeight: 600,
            lineHeight: 1.05,
            letterSpacing: "-0.028em",
          }}
        >
          {value}
        </Typography>
        <Typography variant="subtitle2" sx={{ letterSpacing: "-0.01em" }}>
          {label}
        </Typography>
        {hint ? (
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
        ) : null}
      </Stack>
    </Reveal>
  );
}

export function MediaFrame({ children, className = "", sx, ...rest }) {
  return (
    <Box
      className={`app-media-frame${className ? ` ${className}` : ""}`}
      sx={sx}
      {...rest}
    >
      {children}
    </Box>
  );
}

/* ---------------------------------------------------------------- motion */

/** Infinite, pause-on-hover logo strip. */
export function LogoMarquee({ items = [], height = 40, duration = 42, sx }) {
  if (!items.length) return null;
  const loop = [...items, ...items];

  return (
    <Box
      className="app-marquee"
      sx={sx}
      style={{ "--app-marquee-duration": `${duration}s` }}
    >
      <Box className="app-marquee-track">
        {loop.map((item, index) => (
          <Box
            key={`${item.src || item.alt || index}-${index}`}
            component="img"
            src={item.src}
            alt={item.alt || ""}
            loading="lazy"
            decoding="async"
            aria-hidden={index >= items.length ? "true" : undefined}
            sx={{
              height,
              width: "auto",
              maxWidth: 160,
              objectFit: "contain",
              opacity: 0.62,
              filter: "grayscale(1)",
              transition:
                "opacity var(--app-motion-base), filter var(--app-motion-base)",
              "&:hover": { opacity: 1, filter: "none" },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/** Three chevrons hinting that more content follows. */
export function ScrollHint({ sx }) {
  return (
    <Box className="app-scroll-hint" aria-hidden="true" sx={sx}>
      <KeyboardArrowDownRoundedIcon sx={{ fontSize: 30 }} />
      <KeyboardArrowDownRoundedIcon sx={{ fontSize: 30 }} />
      <KeyboardArrowDownRoundedIcon sx={{ fontSize: 30 }} />
    </Box>
  );
}

export { Reveal };
